import { keccak_256 } from "@noble/hashes/sha3.js";

import { CCTPTransientError } from "../errors";
import { bytesToHex, getMessageTransmitterContract, getRpcForDomain } from "./contracts";

/**
 * Computes the `usedNonces` mapping key for a given source domain and nonce.
 * key = keccak256(abi.encodePacked(uint32(sourceDomain), uint64(nonce)))
 */
function computeNonceKey(sourceDomain: number, nonce: number): string {
  // abi.encodePacked(uint32, uint64) = 4 + 8 = 12 bytes
  const packed = new Uint8Array(12);
  const view = new DataView(packed.buffer);
  view.setUint32(0, sourceDomain, false); // big-endian
  // uint64 big-endian: split into high/low 32-bit words (nonce fits in safe integer range)
  const high = Math.floor(nonce / 0x100000000);
  const low = nonce >>> 0;
  view.setUint32(4, high, false);
  view.setUint32(8, low, false);

  const hash = keccak_256(packed);
  return "0x" + bytesToHex(hash);
}

/** Function selector for `usedNonces(bytes32)` — precomputed at module load */
const USED_NONCES_SELECTOR = (() => {
  const hash = keccak_256(new TextEncoder().encode("usedNonces(bytes32)"));
  return "0x" + bytesToHex(hash.slice(0, 4));
})();

/**
 * Check if a CCTP nonce has been consumed on an EVM destination chain by calling
 * the V1 MessageTransmitter's `usedNonces(bytes32)` via `eth_call`.
 *
 * Throws `CCTPTransientError` on RPC failure so a transient outage isn't
 * treated as "not redeemed". Returns `null` for unsupported EVM destinations.
 */
export async function checkNonceUsedOnEvm(
  sourceDomain: number,
  nonce: number,
  destDomain: number,
): Promise<boolean | null> {
  const contract = getMessageTransmitterContract(destDomain);
  const rpc = getRpcForDomain(destDomain);
  if (!contract || !rpc) return null;

  const nonceKey = computeNonceKey(sourceDomain, nonce);
  // calldata = selector(4) + bytes32 key (already 32 bytes from keccak)
  const calldata = USED_NONCES_SELECTOR + nonceKey.slice(2);

  let response: Response;
  try {
    response = await fetch(rpc, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "eth_call",
        params: [{ to: contract, data: calldata }, "latest"],
      }),
    });
  } catch (err) {
    throw new CCTPTransientError(
      `EVM nonce RPC request failed (sourceDomain=${sourceDomain}, nonce=${nonce}, destDomain=${destDomain})`,
      err,
    );
  }

  if (!response.ok) {
    throw new CCTPTransientError(
      `EVM nonce RPC returned ${response.status} (sourceDomain=${sourceDomain}, nonce=${nonce}, destDomain=${destDomain})`,
    );
  }

  let data: { result?: string; error?: unknown };
  try {
    data = (await response.json()) as typeof data;
  } catch (err) {
    throw new CCTPTransientError(
      `EVM nonce RPC returned invalid JSON (sourceDomain=${sourceDomain}, nonce=${nonce}, destDomain=${destDomain})`,
      err,
    );
  }

  if (data.error || !data.result) return null;

  // Result is uint256 encoded as hex. Non-zero (1) means nonce was consumed.
  return BigInt(data.result) > BigInt(0);
}
